import AuthRoute from "components/auth/AuthRoute";
import AdminRoute from "components/auth/AdminRoute";
import { Events, EditEvent, CreateEvent, DuplicateEvent } from "pages/Events";
import { Home } from "pages/Home";
import { Login } from "pages/auth";
import { EditUser } from "pages/users";
import { Users } from "pages/users/Users";
import { CreateVenue, EditVenue, Venues } from "pages/venues";
import { Route, Routes } from "react-router-dom";
import { CreateOrganizer, EditOrganizer, Organizers } from "pages/organizers";
import { CreateAdvertiser, Advertisers, EditAdvertiser } from "pages/advertisers";
import { CreateCreative, Creatives, EditCreative } from "pages/creatives";
import { CreateCampaignPage, CampaignsPage, CampaignDetailPage, EditCampaignPage } from "pages/campaigns";
import React, { Suspense, useEffect } from "react";
import { Center, Loader } from "@mantine/core";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n/i18n";
import { ResetPassword } from "pages/reset-password";
import { Applications, ApplicationEdit } from "pages/Applications";
import { Beats } from "pages/Beats";
import { Desk, DeskCandidate } from "pages/Desk";

// Loading component for suspense fallback
const Loader18n: React.FC = () => (
  <Center style={{ width: "100%", height: "100vh" }}>
    <Loader size="xl" />
  </Center>
);

// ErrorBoundary for catching i18n errors
class I18nErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("I18n error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Center style={{ width: "100%", height: "100vh" }}>
          <div>
            <h2>Something went wrong with translations.</h2>
            <button onClick={() => this.setState({ hasError: false })}>
              Try again
            </button>
          </div>
        </Center>
      );
    }

    return this.props.children;
  }
}

export const App = () => {
  // Ensure i18n is initialized
  useEffect(() => {
    if (!i18n.isInitialized) {
      console.warn("i18n is not initialized, initializing now");
      i18n
        .init()
        .catch((err) => console.error("Failed to initialize i18n:", err));
    }
  }, []);

  return (
    <I18nErrorBoundary>
      <Suspense fallback={<Loader18n />}>
        <I18nextProvider i18n={i18n}>
          <Routes>
            <Route element={<AuthRoute />}>
              <Route path="/" element={<Home />}>
                <Route path="/Events" element={<Events />} />
                <Route path="/Events/:uid" element={<EditEvent />} />
                <Route path="/Events/create" element={<CreateEvent />} />
                <Route
                  path="/Events/:uid/duplicate"
                  element={<DuplicateEvent />}
                />
                <Route path="/Users" element={<Users />} />
                <Route path="/Users/:uid" element={<EditUser />} />
                <Route path="/Organizers" element={<Organizers />} />
                <Route path="/Organizers/:uid" element={<EditOrganizer />} />
                <Route
                  path="/Organizers/create"
                  element={<CreateOrganizer />}
                />
                <Route path="/profile" element={<EditOrganizer />} />
                <Route path="/Venues" element={<Venues />} />
                <Route path="/Venues/:uid" element={<EditVenue />} />
                <Route path="/Venues/create" element={<CreateVenue />} />
                <Route path="/Advertisers" element={<Advertisers />} />
                <Route path="/Advertisers/:id" element={<EditAdvertiser />} />
                <Route path="/Advertisers/create" element={<CreateAdvertiser />} />
                <Route path="/Creatives" element={<Creatives />} />
                <Route path="/Creatives/:id" element={<EditCreative />} />
                <Route path="/Creatives/create" element={<CreateCreative />} />
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/campaigns/create" element={<CreateCampaignPage />} />
                <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="/campaigns/:id/edit" element={<EditCampaignPage />} />
                <Route element={<AdminRoute />}>
                  <Route path="/desk" element={<Desk />} />
                  <Route path="/desk/:id" element={<DeskCandidate />} />
                  <Route path="/applications" element={<Applications />} />
                  <Route path="/applications/:id" element={<ApplicationEdit />} />
                  <Route path="/beats" element={<Beats />} />
                </Route>
              </Route>
            </Route>
            {/* <Route path="/register" element={<Register />} /> */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* <Route path="/passwordreset" element={<PasswordReset />} /> */}
            {/* <Route path="/update-password" element={<UpdatePassword />} /> */}
          </Routes>
        </I18nextProvider>
      </Suspense>
    </I18nErrorBoundary>
  );
};
